import React, { Fragment, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import { MutationFunction } from 'react-apollo';
import { withGroupModerators, IWithGroupModerators } from 'providers/queries';

import { addModerator, removeModerator } from 'apollo/reducers';
import { AddUser, RemoveUser } from 'components';
import { useNotify } from 'hooks';
import {
  CreateGroupModeratorComponent,
  DeleteGroupModeratorComponent,
  GetGroupModeratorsDocument,
  GroupModerator,
  ProfileSuggestion,
  PublicUserProfile,
} from 'api';
import { deleteGroupModeratorResponse } from 'apollo/responses';

import { moderatorIsOwner } from '../helpers';

interface IProps {
  groupId: string;
}

interface IPropsEnhanced extends IProps, IWithGroupModerators {}

const Moderators: FC<IPropsEnhanced> = ({ groupId, groupModerators }) => {
  const { t } = useTranslation();
  const notify = useNotify();

  function getModeratorIds() {
    return groupModerators.map(({ user_id }) => user_id);
  }

  function isOwner(moderator: GroupModerator) {
    return moderatorIsOwner(moderator);
  }

  function handleAdd(moderator: ProfileSuggestion, mutation: MutationFunction) {
    return mutation({
      variables: {
        user_id: moderator.id,
        group_id: groupId,
      },
      update: (cache, result) => {
        addModerator({
          cache,
          result,
          query: GetGroupModeratorsDocument,
          variables: { group_id: groupId },
        });
      },
    });
  }

  function handleRemove(moderator: PublicUserProfile, mutation: MutationFunction) {
    return mutation({
      variables: {
        user_id: moderator.id,
        group_id: groupId,
      },
      update: (cache, result) => {
        // TODO: Hack - ID should be returned by the server
        removeModerator({
          cache,
          result,
          query: GetGroupModeratorsDocument,
          variables: { group_id: groupId },
          id: moderator.id,
        });
      },
      optimisticResponse: deleteGroupModeratorResponse(),
    });
  }

  return (
    <Fragment>
      <Typography variant="subtitle2">{t('groups:moderators')}</Typography>
      <CreateGroupModeratorComponent onError={notify.mutationError}>
        {mutation => (
          <AddUser exclusions={getModeratorIds()} onChange={user => handleAdd(user, mutation)} />
        )}
      </CreateGroupModeratorComponent>
      <DeleteGroupModeratorComponent onError={notify.mutationError}>
        {mutation => (
          <Fragment>
            {groupModerators.map(groupModerator => (
              <RemoveUser
                key={groupModerator.user_id}
                user={groupModerator.user}
                canRemove={!isOwner(groupModerator)}
                onRemove={user => handleRemove(user, mutation)}
                removeKey="groups:removeModeratorAlert"
                confirmKey="common:delete"
              />
            ))}
          </Fragment>
        )}
      </DeleteGroupModeratorComponent>
    </Fragment>
  );
};

export default withGroupModerators(Moderators);
